import { Size } from '@ps/web-core-enums';
import { select } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import getScrollbarWidth from 'get-scrollbar-width';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import BodyClassName from 'react-body-classname';
import { useSelector } from 'react-redux';

import styles from './ScreenSizeProvider.module.scss';

interface WindowSize {
  width: number;
  height: number;
}

interface WindowPosition {
  top: number;
  left: number;
}

export interface ScreenSizeContextValue {
  screenSize: Size;
  windowSize: WindowSize;
  windowPosition: WindowPosition;
  greaterThan(size: Size): boolean;
}

const sizes = Object.keys(Size).map((size) => Size[size as keyof typeof Size]);

const getSizeFromWindow = () =>
  window
    .getComputedStyle(document.body, ':after')
    .getPropertyValue('content')
    .replace(/"/g, '') as Size;

const getWindowSize = () => ({
  width: canUseDOM ? window.innerWidth : 0,
  height: canUseDOM ? window.innerHeight : 0
});

const getWindowPosition = () => ({
  top: canUseDOM ? window.screenY : 0,
  left: canUseDOM ? window.screenX : 0
});

export const ScreenSizeContext = React.createContext<ScreenSizeContextValue>({
  screenSize: Size.Large,
  windowSize: getWindowSize(),
  windowPosition: getWindowPosition(),
  greaterThan: () => true
});

const ScreenSizeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isMobile = useSelector(select.device.isMobile);
  // Approximate screen size on SSR
  const [size, setSize] = useState<Size>(isMobile ? Size.Small : Size.Large);
  const [windowSize, setWindowSize] = useState<WindowSize>(getWindowSize());
  const [windowPosition, setWindowPosition] = useState<WindowPosition>(getWindowPosition());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(
    debounce(() => {
      const actualHeight = window.innerHeight;
      const controlHeight = document.querySelector('#control-height')?.clientHeight || actualHeight;
      const mobileBarsHeight = Math.abs(controlHeight - actualHeight);
      const vh = actualHeight * 0.01;

      setWindowSize(getWindowSize());
      setWindowPosition(getWindowPosition());
      setSize(getSizeFromWindow());
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('--mobileBarsHeight', `${mobileBarsHeight}px`);
    }, 50),
    []
  );

  const greaterThan = useCallback(
    (testSize: Size) => sizes.indexOf(size) > sizes.indexOf(testSize),
    [size]
  );

  useEffect(() => {
    document.documentElement.style.setProperty('--scrollbar-width', `${getScrollbarWidth()}px`);
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, [handleResize]);

  return (
    <>
      <BodyClassName className={styles.body} />
      <ScreenSizeContext.Provider
        value={{ windowSize, windowPosition, screenSize: size, greaterThan }}
      >
        {children}
      </ScreenSizeContext.Provider>
    </>
  );
};

export default ScreenSizeProvider;
